import type { GatherTarget } from '../core/commands';
import { tileCenter, unitsToTile } from '../core/fixed';
import type { ResourceKind } from '../data/types';
import {
  isAdjacentToFootprint,
  isComplete,
  nearestFootprintTile,
  type Building,
} from '../entities/building';
import type { Player } from '../entities/player';
import type { Unit } from '../entities/unit';
import { findNearestEdgeForest, isForest, takeLumber, type GameMap } from '../map/gameMap';
import { heuristic, type Pathfinder } from '../map/pathfinding';
import type { OccupancyFn } from '../map/unitGrid';
import { planPath } from './movement';

/** How far (in tiles) a worker looks for the next tree after its patch runs dry. */
export const TREE_SEARCH_RADIUS = 8;

/** Neighbor order for "which adjacent tree do I chop": fixed for determinism. */
const NEIGHBORS: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [0, 1],
  [-1, 0],
  [0, -1],
  [1, 1],
  [1, -1],
  [-1, 1],
  [-1, -1],
];

/** What the gathering system needs from the simulation. `Sim` satisfies this. */
export interface GatherContext {
  readonly map: GameMap;
  readonly pathfinder: Pathfinder;
  getBuilding(id: number): Building | undefined;
  buildings(): Building[];
  player(owner: number): Player;
  /** Standing units other than `unit`, as obstacles. */
  occupancyFor(unit: Unit): OccupancyFn;
}

function resourceOf(target: GatherTarget): ResourceKind {
  return target.kind === 'mine' ? 'gold' : 'lumber';
}

function walkToBuilding(ctx: GatherContext, unit: Unit, b: Building): void {
  const t = nearestFootprintTile(b, unitsToTile(unit.x), unitsToTile(unit.y));
  unit.path = planPath(unit, ctx.map, ctx.pathfinder, tileCenter(t.x), tileCenter(t.y), {
    occupancy: ctx.occupancyFor(unit),
    accept: (tx, ty) => isAdjacentToFootprint(b, tx, ty),
  });
}

/** Aim at the target itself; pathfinding stops on the nearest reachable tile beside it. */
function walkToTarget(ctx: GatherContext, unit: Unit, target: GatherTarget): boolean {
  if (target.kind === 'mine') {
    const mine = ctx.getBuilding(target.buildingId);
    if (!mine) return false;
    walkToBuilding(ctx, unit, mine);
  } else {
    const { x, y } = target;
    unit.path = planPath(unit, ctx.map, ctx.pathfinder, tileCenter(x), tileCenter(y), {
      occupancy: ctx.occupancyFor(unit),
      accept: (tx, ty) => Math.max(Math.abs(tx - x), Math.abs(ty - y)) === 1,
    });
  }
  return true;
}

function isAtBuilding(unit: Unit, b: Building): boolean {
  return isAdjacentToFootprint(b, unitsToTile(unit.x), unitsToTile(unit.y));
}

function isAdjacentTile(unit: Unit, tx: number, ty: number): boolean {
  const ux = unitsToTile(unit.x);
  const uy = unitsToTile(unit.y);
  return Math.max(Math.abs(ux - tx), Math.abs(uy - ty)) === 1;
}

/** A standing tree next to the unit: the intended one if adjacent, else the first in fixed order. */
function adjacentTree(
  ctx: GatherContext,
  unit: Unit,
  preferred: { x: number; y: number },
): { x: number; y: number } | null {
  if (isAdjacentTile(unit, preferred.x, preferred.y) && isForest(ctx.map, preferred.x, preferred.y))
    return preferred;
  const ux = unitsToTile(unit.x);
  const uy = unitsToTile(unit.y);
  for (const [dx, dy] of NEIGHBORS) {
    if (isForest(ctx.map, ux + dx, uy + dy)) return { x: ux + dx, y: uy + dy };
  }
  return null;
}

/** Does the target still have anything to give? */
function targetHasResource(ctx: GatherContext, target: GatherTarget): boolean {
  if (target.kind === 'mine') {
    const mine = ctx.getBuilding(target.buildingId);
    return mine !== undefined && mine.def.mine === 'gold' && mine.gold > 0;
  }
  return isForest(ctx.map, target.x, target.y);
}

/** Nearest friendly drop-off for the resource, by straight-line tile distance; ties → lowest id. */
export function nearestDropOff(
  ctx: GatherContext,
  unit: Unit,
  resource: ResourceKind,
): Building | null {
  const tx = unitsToTile(unit.x);
  const ty = unitsToTile(unit.y);
  let best: Building | null = null;
  let bestDist = Infinity;
  for (const b of ctx.buildings()) {
    if (b.owner !== unit.owner || !isComplete(b) || !b.def.dropOff.includes(resource)) continue;
    const t = nearestFootprintTile(b, tx, ty);
    const d = heuristic(tx, ty, t.x, t.y);
    if (d < bestDist) {
      best = b;
      bestDist = d;
    }
  }
  return best;
}

/** Start gathering from `target`. Returns false (and leaves the unit alone) if it cannot. */
export function beginGather(ctx: GatherContext, unit: Unit, target: GatherTarget): boolean {
  if (!unit.def.gather || !targetHasResource(ctx, target)) return false;
  unit.hidden = false;
  if (unit.carrying && unit.carrying.amount > 0) {
    // Already carrying: deliver first, then go to this target.
    const drop = nearestDropOff(ctx, unit, unit.carrying.resource);
    if (!drop) return false;
    unit.state = {
      kind: 'gathering',
      target,
      phase: 'toDropOff',
      ticksLeft: 0,
      dropOffId: drop.id,
    };
    walkToBuilding(ctx, unit, drop);
    return true;
  }
  unit.state = { kind: 'gathering', target, phase: 'toResource', ticksLeft: 0, dropOffId: null };
  walkToTarget(ctx, unit, target);
  return true;
}

function goIdle(unit: Unit): void {
  unit.hidden = false;
  unit.state = { kind: 'idle' };
}

/** Head for the next unit of work, or idle when the target is spent and nothing is nearby. */
function continueOrIdle(ctx: GatherContext, unit: Unit, target: GatherTarget): void {
  let next: GatherTarget | null = targetHasResource(ctx, target) ? target : null;
  if (!next && target.kind === 'tree') {
    const t = findNearestEdgeForest(ctx.map, target.x, target.y, TREE_SEARCH_RADIUS);
    if (t) next = { kind: 'tree', x: t.x, y: t.y };
  }
  if (!next) {
    goIdle(unit);
    return;
  }
  unit.state = {
    kind: 'gathering',
    target: next,
    phase: 'toResource',
    ticksLeft: 0,
    dropOffId: null,
  };
  walkToTarget(ctx, unit, next);
}

/** Runs after movement each tick for units in the 'gathering' state. */
export function stepGathering(ctx: GatherContext, unit: Unit): void {
  if (unit.state.kind !== 'gathering' || !unit.def.gather) return;
  const { gather } = unit.def;
  const { target } = unit.state;

  switch (unit.state.phase) {
    case 'toResource': {
      if (unit.path) return; // still walking
      if (target.kind === 'mine') {
        const mine = ctx.getBuilding(target.buildingId);
        if (!mine || mine.gold <= 0 || !isAtBuilding(unit, mine)) {
          goIdle(unit);
          return;
        }
        unit.hidden = true;
        unit.state = { ...unit.state, phase: 'working', ticksLeft: gather.miningTicks };
        return;
      }
      const tree = adjacentTree(ctx, unit, target);
      if (!tree) {
        goIdle(unit);
        return;
      }
      unit.state = {
        ...unit.state,
        target: { kind: 'tree', ...tree },
        phase: 'working',
        ticksLeft: gather.choppingTicks,
      };
      return;
    }
    case 'working': {
      if (unit.state.ticksLeft > 1) {
        unit.state = { ...unit.state, ticksLeft: unit.state.ticksLeft - 1 };
        return;
      }
      let taken = 0;
      if (target.kind === 'mine') {
        const mine = ctx.getBuilding(target.buildingId);
        if (mine) {
          taken = Math.min(gather.carry, mine.gold);
          mine.gold -= taken;
        }
        unit.hidden = false;
      } else {
        taken = takeLumber(ctx.map, target.x, target.y, gather.carry);
      }
      if (taken === 0) {
        // Someone else cleared it under us; look for more work.
        continueOrIdle(ctx, unit, target);
        return;
      }
      unit.carrying = { resource: resourceOf(target), amount: taken };
      const drop = nearestDropOff(ctx, unit, unit.carrying.resource);
      if (!drop) {
        goIdle(unit);
        return;
      }
      unit.state = { ...unit.state, phase: 'toDropOff', ticksLeft: 0, dropOffId: drop.id };
      walkToBuilding(ctx, unit, drop);
      return;
    }
    case 'toDropOff': {
      if (unit.path) return;
      const drop =
        unit.state.dropOffId === null ? undefined : ctx.getBuilding(unit.state.dropOffId);
      if (!drop || !isAtBuilding(unit, drop)) {
        goIdle(unit);
        return;
      }
      if (unit.carrying) {
        const player = ctx.player(unit.owner);
        if (unit.carrying.resource === 'gold') player.gold += unit.carrying.amount;
        else player.lumber += unit.carrying.amount;
        unit.carrying = null;
      }
      continueOrIdle(ctx, unit, target);
      return;
    }
  }
}
